import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Checkbox, FormControlLabel, Typography, Box } from '@mui/material';
import { TextField } from '@material-ui/core';
import { updateDisclosure } from '../../../store/modules/generalData/actions';

// simple id generator
const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 5);


function Disclosure({ disclosure, dispatchUpdateDisclosure }) {
  // ensure initial structure
  useEffect(() => {
    if (!disclosure.typesUsed || disclosure.typesUsed.length === 0) {
      // initialize with defaults + one blank custom
      const defaults = [
        { id: 'Audited', label: 'Audited', checked: false, isCustom: false },
        { id: 'Reviewed', label: 'Reviewed', checked: false, isCustom: false },
        { id: 'Compiled', label: 'Compiled', checked: false, isCustom: false },
        { id: 'Tax Returns', label: 'Tax Returns', checked: false, isCustom: false },
        { id: 'In-House', label: 'In-House', checked: false, isCustom: false },
      ];
      const initial = [
        ...defaults,
        { id: generateId(), label: '', checked: false, isCustom: true },
      ];
      dispatchUpdateDisclosure('typesUsed', initial);
    }
  }, []);

  // Handlers for “Types of Financial Statements Used”
  const handleDefaultTypeChange = (id) => {
    const updated = disclosure.typesUsed.map(item =>
      item.id === id ? { ...item, checked: !item.checked } : item
    );
    dispatchUpdateDisclosure('typesUsed', updated);
  };

  const handleCustomTypeCheckChange = (id) => {
    const updated = disclosure.typesUsed.map(item =>
      item.id === id ? { ...item, checked: !item.checked } : item
    );
    dispatchUpdateDisclosure('typesUsed', updated);
  };

  const handleCustomTypeLabelChange = (id, newLabel) => {
    // 1) update the label
    let temp = disclosure.typesUsed.map(item =>
      item.id === id ? { ...item, label: newLabel } : item
    );
    // 2) if the last custom now has text, append a new blank
    const customItems = temp.filter(i => i.isCustom);
    const lastCustom = customItems[customItems.length - 1];
    if (lastCustom && lastCustom.label.trim() !== '') {
      temp = [
        ...temp,
        { id: generateId(), label: '', checked: false, isCustom: true },
      ];
    }
    // 3) rebuild array: defaults in original order, filled customs, and at most one blank custom
    const defaults = temp.filter(i => !i.isCustom);
    const filled = temp.filter(i => i.isCustom && i.label.trim() !== '');
    const blanks = temp.filter(i => i.isCustom && i.label.trim() === '');
    const finalTypesUsed = [...defaults, ...filled];
    if (blanks.length) {
      // keep only the last blank
      finalTypesUsed.push(blanks[blanks.length - 1]);
    }
    dispatchUpdateDisclosure('typesUsed', finalTypesUsed);
  };

  const handleChange = (name) => {
    dispatchUpdateDisclosure(name, !disclosure[name]);
  };

  return (
    <Box className="p-6 bg-white rounded shadow">
      {/* 1. Types of Financial Statements Used */}
      <Box className="mb-10">
        <h3 className="font-semibold mb-2">Types of Financial Statements Used</h3>
        {disclosure.typesUsed && disclosure.typesUsed.map(item =>
          item.isCustom ? (
            <Box key={item.id} className="flex items-center mb-2">
              <Checkbox
                checked={item.checked}
                onChange={() => handleCustomTypeCheckChange(item.id)}
                color="primary"
              />
              <TextField
                value={item.label}
                onChange={e => handleCustomTypeLabelChange(item.id, e.target.value)}
                placeholder="Other (please specify)"
                variant="outlined"
                size="small"
                style={{ width: '300px' }}
                className="ml-2"
              />
            </Box>
          ) : (
            <div className="ml-3">
              <FormControlLabel
                key={item.id}
                control={
                  <Checkbox
                    checked={item.checked}
                    onChange={() => handleDefaultTypeChange(item.id)}
                    color="primary"
                  />
                }
                label={item.label}
                className="block mb-2"
              />
            </div>
          )
        )}
      </Box>

      {/* 2. Conflict of Interest Check */}
      <Box className="mb-10">
        <h3 className="font-semibold mb-2">Conflict of Interest Check</h3>
        <div className="ml-3">
          <FormControlLabel
            control={
              <Checkbox
                checked={disclosure.conflictOneChecked}
                onChange={(e) => handleChange("conflictOneChecked")}
                color="primary"
              />
            }
            label="The Valuation Analyst did not know or have a personal relationship with Client prior to engaging in this valuation assignment."
            className="block"
          />
        </div>
        <div className="ml-3">
          <FormControlLabel
            control={
              <Checkbox
                checked={disclosure.conflictTwoChecked}
                onChange={(e) => handleChange("conflictTwoChecked")}
                color="primary"
              />
            }
            label="The Valuation Analyst did not have any personal knowledge about the business, which would cause our valuation firm to have information that another firm could not readily have."
            className="block"
          />
        </div>
        <div className="ml-3">
          <FormControlLabel
            control={
              <Checkbox
                checked={disclosure.conflictThreeChecked}
                onChange={(e) => handleChange("conflictThreeChecked")}
                color="primary"
              />
            }
            label="The Valuation Analyst is not currently engaged and will not be providing any further ongoing services to include:"
            className="block"
          />
          <div className="ml-8"><Typography>- Business Brokering</Typography></div>
          <div className="ml-8"><Typography>- Management Consulting</Typography></div>
          <div className="ml-8"><Typography>- Tax Preparation</Typography></div>
          <div className="ml-8"><Typography>- Bookkeeping</Typography></div>
          <div className="ml-8"><Typography>- Other Consulting Services</Typography></div>
        </div>
        <div className="ml-3">
          <FormControlLabel
            control={
              <Checkbox
                checked={disclosure.conflictFourChecked}
                onChange={(e) => handleChange("conflictFourChecked")}
                color="primary"
              />
            }
            label="The Valuation Analyst has not conducted a previous valuation of the clients within a three-year period."
            className="block"
          />
        </div>
        <div className="ml-3">
          <FormControlLabel
            control={
              <Checkbox
                checked={disclosure.conflictFiveChecked}
                onChange={(e) => handleChange("conflictFiveChecked")}
                color="primary"
              />
            }
            label="The Valuation Analyst's integrity of independence was not compromised in generating the Conclusion of Value."
            className="block"
          />
        </div>
      </Box>

      {/* 3. Disclosure Of Any Contingency Fee */}
      <Box className="mb-10">
        <h3 className="font-semibold mb-2">Disclosure Of Any Contingency Fee</h3>
        <div className="ml-3">
          <FormControlLabel
            control={
              <Checkbox
                checked={disclosure.contingencyChecked}
                onChange={(e) => handleChange("contingencyChecked")}
                color="primary"
              />
            }
            label="Only a fee was paid to the Valuation Analyst for their work on this valuation report. This charge was not contingent on the Conclusion of Value nor was it based on it."
            className="block"
          />
        </div>
      </Box>

      {/* 4. A Statement Of Financial Interest */}
      <Box>
        <h3 className="font-semibold mb-2">A Statement Of Financial Interest</h3>
        <div className="ml-3">
          <FormControlLabel
            control={
              <Checkbox
                checked={disclosure.interestChecked}
                onChange={(e) => handleChange("interestChecked")}
                color="primary"
              />
            }
            label="The Valuation Analyst does not have any present interests in the company that is being valued."
            className="block"
          />
        </div>
      </Box>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  disclosure: state.generalData.disclosure
});

const mapDispatchToProps = (dispatch) => ({
  dispatchUpdateDisclosure(name, value) {
    dispatch(updateDisclosure(name, value));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(Disclosure);